import React, { createContext, useContext, useMemo, useState } from "react";

export type ThemeMode = "light" | "dark";

type ThemeModeContextType = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
};

const STORAGE_KEY = "translatorx_theme_mode";

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: "light",
  setMode: () => {},
  toggleMode: () => {},
});

function readInitialMode(): ThemeMode {
  try {
    const savedMode = localStorage.getItem(STORAGE_KEY);
    if (savedMode === "dark" || savedMode === "light") {
      return savedMode;
    }
  } catch (error) {
    // Ignore storage access errors and fall back to light mode.
  }

  return "light";
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(() => readInitialMode());

  const setMode = (nextMode: ThemeMode) => {
    setModeState(nextMode);
    try {
      localStorage.setItem(STORAGE_KEY, nextMode);
    } catch (error) {
      // Ignore storage access errors so theme switching still works in-memory.
    }
  };

  const value = useMemo(
    () => ({
      mode,
      setMode,
      toggleMode: () => setMode(mode === "light" ? "dark" : "light"),
    }),
    [mode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeModeContext = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error(
      "useThemeModeContext must be used within a ThemeModeProvider",
    );
  }
  return context;
};
